/* eslint-disable @typescript-eslint/no-this-alias */
import _ from "lodash";

import { Bar } from "app/charts/Bar";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { isDurationAfterSplit } from "app/pages/website-analysis/traffic-sources/mmx/components/common/MobileSplit-utils";
import {
    NUMBERS_DATA_FORMAT,
    PERCENT_DATA_FORMAT,
} from "app/pages/website-performance/components/ChannelsOverview/ChannelsOverviewBarChartContainer";
import type {
    IPptBarChartRequest,
    IPptSeriesData,
    IPptSlideExportRequest,
} from "app/services/PptExportService/PptExportServiceTypes";
import { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

import MmxDashboardBarChartDataFetcherFactory from "../widget-fetchers/MmxDashboardBarChartDataFetcher";
import BarChartMMXDashboardWidgetFilters from "../widget-filters/BarChartMMXDashboardWidgetFilters";
import { getBarChartWidgetPptOptions } from "../widget-utilities/widgetPpt/PptBarChartWidgetUtils";
import { getWidgetSubtitle, getWidgetTitle, resolveYAxisFormat } from "../widget-utilities/widgetPpt/PptWidgetUtils";

import { BarChartWidget } from "./BarChartWidget";

export class BarChartMMXDashboardWidget extends BarChartWidget {
    public isPptSupported = () => {
        return this.chartConfig.series && this.chartConfig.series.length > 0;
    };

    private getWidgetDataFormat = (): string => {
        const widgetFormat = this._widgetConfig?.properties?.dataMode
            ? this._widgetConfig.properties.dataMode
            : this._defaultDataFormat || "percent";
        return widgetFormat;
    };

    public getDataForPpt = (): IPptSlideExportRequest => {
        return {
            title: getWidgetTitle(this),
            subtitle: getWidgetSubtitle(this, { showEntities: !this.isCompare() }),
            type: "bar",
            details: {
                format: resolveYAxisFormat(this.getWidgetDataFormat()),
                options: getBarChartWidgetPptOptions(),
                data: this.isCompare() ? this.getCompareModeSeriesDataForPpt() : this.getSingleModeSeriesDataForPpt(),
            } as IPptBarChartRequest,
        };
    };

    private getCompareModeSeriesDataForPpt = (): IPptSeriesData[] => {
        const isPercentFormat = this.getWidgetDataFormat() === "percent";

        return this.chartConfig.series.map(
            (series: { name: string; color: string; data: Array<{ key: string; y: number }> }, index: number) => {
                const { name, data } = series;
                const labels = data.map((point) => point.key);
                const values = data.map((point) => (isPercentFormat ? point.y / 100 : point.y));
                const color = this.chartConfig.options.colors[index];
                return {
                    seriesName: name,
                    seriesColor: color,
                    labels,
                    values,
                };
            },
        );
    };

    private getSingleModeSeriesDataForPpt = (): IPptSeriesData[] => {
        const isPercentFormat = this.getWidgetDataFormat() === "percent";
        const series = this.chartConfig.series[0];

        return series.data.map((record: { key: string; y: number; color: string }) => {
            const yValue = isPercentFormat ? record.y / 100 : record.y;

            return {
                seriesName: record.key,
                seriesColor: record.color,
                labels: [series.name],
                values: [yValue],
            };
        });
    };

    constructor() {
        super();
    }

    static getWidgetMetadataType() {
        return "BarChartMMXDashboard";
    }

    static getWidgetDashboardType() {
        return "BarChart";
    }

    static getFiltersComponent() {
        return BarChartMMXDashboardWidgetFilters;
    }

    get templateUrl() {
        return "/app/components/widget/widget-templates/barchartmmx.html";
    }

    private dataFetcherFactory() {
        return MmxDashboardBarChartDataFetcherFactory;
    }
    public getFetcherFactory() {
        return this.dataFetcherFactory();
    }

    public async reCreateChartConfig() {
        const formattedData = await this.getChartSeries(this.originalData);
        const bar = new Bar(
            this.metadata.chartOptions,
            formattedData,
            this._widgetConfig.properties.dataMode,
            this._params.webSource,
        );
        this.chartConfig = _.merge({}, bar.getChartConfig(), this.getOptionsChartConfig());
        this.applyLegendColorsToChartConfig(this.chartConfig);
    }

    public getChartSeries(unorderedData: any): any {
        if (this._params.webSource === devicesTypes.DESKTOP) {
            return super.getChartSeries(unorderedData);
        }
        if (this._params.webSource === devicesTypes.MOBILE) {
            if (isDurationAfterSplit(this._params.from)) {
                // const formattedDate = this._params.from.replaceAll("|", "-");
                // if (dayjs(formattedDate).isAfter(SplitChannels.JUNE2020)) {
                return super.getChartSeries(unorderedData);
            }
        }
        // Collect every channel key that appears across all domains so each series has the
        // same set of keys. Without this, domains with extra channels (e.g. Social) end up
        // rendered at indexes that the xAxis (built from the first domain only) doesn't
        // label, causing Highcharts to fall back to numeric "7"/"8" tick labels.
        const allChannelKeys = new Set<string>();
        _.forEach(unorderedData, (v) => {
            if (_.isPlainObject(v)) {
                Object.keys(v).forEach((key) => allChannelKeys.add(key));
            }
        });
        const channelKeyDefaults: Record<string, number> = {};
        allChannelKeys.forEach((key) => {
            channelKeyDefaults[key] = 0;
        });
        const buildSeries = (v: any, k: string) => ({
            data: _.isPlainObject(v) ? { ...channelKeyDefaults, ...v } : { ...channelKeyDefaults },
            name: k,
        });
        const orderedKeys = this.getOrderedCompareKeys(unorderedData);
        return orderedKeys
            ? orderedKeys.map((key) => buildSeries(unorderedData[key], key))
            : _.map(unorderedData, buildSeries);
    }

    public callbackOnGetData(response: any) {
        // condition for dashboard wizard widget
        /*if (this._params.webSource === 'Desktop'){
          return super.callbackOnGetData(response);
        }*/
        const compareKeys = this._params.keys.split(",");
        this.metadata.chartOptions.isCompare = compareKeys.length > 1;
        this.metadata.chartOptions.viewOptions = this._viewOptions;
        this.metadata.chartOptions.linkParams = this.buildLinkParams();
        this.reCreateChartConfig();
    }

    public getOptionsChartConfig() {
        const widget = this;
        return {
            options: {
                chart: {
                    height: 220,
                    margin: [20, 20, 30, 50],
                    spacing: [10, 0, 0, 0],
                },
                yAxis: {
                    min: null,
                    max: widget._widgetConfig.properties.dataMode === "percent" ? 100 : null,
                    endOnTick: true,
                },
                plotOptions: {
                    column: {
                        //groupPadding: this.getGroupPadding(),
                        pointPadding: 0.15,
                        pointWidth: null,
                        maxPointWidth: 40,
                        pointRange: 1,
                        grouping: true,
                        dataLabels: {
                            formatter() {
                                const formattedData = widget.formatGraphPoint(
                                    this.point.y,
                                    widget._widgetConfig.properties.dataMode,
                                    (point) => `${point.toFixed(2)}%`,
                                    (point) => abbrNumberFilter()(point),
                                );
                                return formattedData;
                            },
                        },
                    },
                },
                tooltip: {
                    pointFormatter: function () {
                        const formattedPoint = widget.formatGraphPoint(
                            this.y,
                            widget._widgetConfig.properties.dataMode,
                            (point) => (Math.round(point * 100) / 100).toFixed(2) + "%",
                            (point) => abbrNumberFilter()(point || 0),
                        );

                        const color = this.pointTooltipColor ? this.pointTooltipColor : this.color;
                        return `<span style="color:${color}; font-family: Roboto;">\u25CF </span>${this.series.name}: <span style="font-weight: bold;color:${color};">${formattedPoint}</span><br/>`;
                    },
                },
            },
        };
    }

    public formatGraphPoint(
        pointValue: number,
        pointDataType: string,
        formatPercent: (point: number) => string,
        formatNumber: (point: number) => string,
    ) {
        if (!pointValue || pointValue <= Number.EPSILON) {
            return "N/A";
        }

        switch (pointDataType) {
            case "number":
            case NUMBERS_DATA_FORMAT:
                return formatNumber(pointValue);

            case "percent":
            case PERCENT_DATA_FORMAT:
                return formatPercent(pointValue);

            default:
                return "N/A";
        }
    }
}

BarChartMMXDashboardWidget.register();
